/**
 * EndCard.jsx — DEAD SIGNAL vertical slice
 *
 * Day-5 completion screen in the idiom of a 1989-91 VGA adventure ending
 * (centered double-rule dialog over a flat dark field, blocky shadow, bitmap
 * type, a score line). The card is a case ledger, not a lecture: evidence on
 * file, threads pressed, the instrument's own peak readings, and a vague
 * canon-true tease of tomorrow. It never interprets the readings or names a
 * mechanic (fourth-wall rule, demo doctrine). The telemetry/eval surface
 * stays reachable through the harness button.
 */
import React from 'react';
import { useGameStore } from '../state/gameState.js';
import { useEvalStore } from '../evals/evalStore.js';
import { resetInterviewSession } from '../state/interviewManager.js';
import { getBioscan } from '../systems/bioscan.js';
import { allEvidence } from '../content/evidenceCanon.js';

// Reading-room labels for the interview topics (display only).
const TOPIC_LABELS = {
  courier_manifest: 'the courier manifest',
  chip_donation: 'the chip donation',
  clinical_trial_st_erasmus: 'St. Erasmus',
  celeste_grief: 'Celeste',
  unprompted_denial: 'the unprompted denial',
  interference_conflict: 'the dropped channel',
};
const THREAD_TOPICS = ['courier_manifest', 'chip_donation', 'clinical_trial_st_erasmus'];

export default function EndCard() {
  const demoStage = useGameStore((s) => s.demoStage);
  const interview = useGameStore((s) => s.interview);
  const evidence = useGameStore((s) => s.evidence);
  const replaySlice = useGameStore((s) => s.replaySlice);
  const setDemoStage = useGameStore((s) => s.setDemoStage);
  const exportGoldenSet = useEvalStore((s) => s.exportGoldenSet);
  const exportCsv = useEvalStore((s) => s.exportCsv);
  const evalCount = useEvalStore((s) => s.records.length);

  if (demoStage !== 'end') return null;

  const topics = [...new Set((interview.turns || []).map((t) => t.topic))].filter((t) => t && t !== 'default');
  const threadsPressed = topics.filter((t) => THREAD_TOPICS.includes(t));
  const evidenceTotal = allEvidence().length;

  // Peak instrument readings from the topics the player actually worked —
  // raw numbers off the panel, no interpretation attached.
  const markers = topics
    .map((t) => ({ t, hr: getBioscan(t).heartRate }))
    .filter((m) => m.hr >= 88)
    .sort((a, b) => b.hr - a.hr)
    .slice(0, 3);

  const openEvals = () => {
    setDemoStage('playing');
  };
  const exportData = () => {
    if (evalCount) exportGoldenSet();
    else exportCsv();
  };
  const replay = () => {
    resetInterviewSession();
    replaySlice();
  };

  return (
    <div className="end-card">
      <div className="end-card-scrim" />
      <div className="end-card-frame">
        <div className="end-card-rule">DEAD SIGNAL</div>
        <h2 className="end-card-title">AND SO ENDS DAY 5 IN NEW MERIDIAN.</h2>
        <p className="end-card-lede">
          Hargrove thanks you for coming. The elevator hums all twenty-three floors down.
          Outside, the rain hasn&apos;t let up.
        </p>

        <div className="end-card-ledger">
          <div className="end-row"><span className="end-row-l">EVIDENCE ON FILE</span><span className="end-row-v">{evidence.length} of {evidenceTotal}</span></div>
          <div className="end-row"><span className="end-row-l">THREADS PRESSED</span><span className="end-row-v">{threadsPressed.length ? threadsPressed.map((t) => TOPIC_LABELS[t] || t).join(' · ') : 'none'}</span></div>
          <div className="end-row"><span className="end-row-l">INTERVIEW TURNS</span><span className="end-row-v">{interview.turns?.length || 0}</span></div>
          {markers.length > 0 && (
            <div className="end-row"><span className="end-row-l">BIO-SCAN PEAKS</span><span className="end-row-v">{markers.map((m) => `${m.hr} bpm — ${TOPIC_LABELS[m.t] || m.t}`).join(' · ')}</span></div>
          )}
        </div>

        <p className="end-card-next">
          Tomorrow is another day. The St. Erasmus records are still out there, and someone
          at the university knew Celeste. The clock hasn&apos;t stopped.
          Can Craine trace the dead signal to its source — before it goes quiet for good?
        </p>

        <div className="end-card-score">
          You concluded the interview with {evidence.length} of a possible {evidenceTotal} pieces of evidence.
        </div>

        <div className="end-card-actions">
          <button className="end-btn primary" onClick={openEvals}>[ OPEN THE AI EVALS HARNESS ]</button>
          <button className="end-btn" onClick={exportData}>
            {evalCount ? `[ EXPORT GOLDEN SET (${evalCount}) ]` : '[ EXPORT SESSION CSV ]'}
          </button>
          <button className="end-btn ghost" onClick={replay}>[ REPLAY SLICE ]</button>
        </div>
      </div>
    </div>
  );
}
